import isEmpty from 'lodash/isEmpty';
import { useEffect, useState } from 'react';
import styles from './DirectorDetails.module.scss';
import DirectorInputField from './Components/DirectorInputField';
import { numberOnly } from '../../../../../utils/utils';
import clsx from 'clsx';
import { Input } from '../../../../../common/_custom';
import { useDispatch } from 'react-redux';
import { addDirectorWithoutFetch, sendCibilConsentLink } from '../../../../../store/company/action';
import { useAppSelector } from '../../../../../app/hooks';
import { toast } from 'react-toastify';
import { GREEN_BG_WHITE_TICK_CIRCLE, orangeCross, uploadEquifax, verifyOrange } from '../../../../../assets/hostedassets';
import { lightBulbBlack, PURPLE_CIRCULAR_CROSS, VERIFY_GREY_CIRCLE, VERIFY_PURPLE_CIRCLE } from '../../../../../assets/hostedassets';
import { GREEN_CHECK_SMALL } from '../../../../../assets/hostedassets';
import { Mixpanel } from '../../../../../utils/mixpanel';
import { COMPANY_CB_ADD_DIRECTOR, COMPANY_CB_VERIFY_DIRECTOR } from '../../../../../utils/constants/mixpanelEvents/companyEvents';

import { useParams } from 'react-router-dom';
import LoadingSpinner from '../../../../../common/_custom/LoadingSpinner/LoadingSpinner';
import Tooltip from '../../../../../common/_custom/Tooltip/Tooltip';
import { USER_TYPE } from '../../../../../utils/constants/user';
import CrossFilledIcon from '../../../../../assets/Svg/crossFilledIcon';
import FetchFillIcon from '../../../../../assets/Svg/FetchFilIcon';
import { validatePAN } from '../../../../../utils/validatePAN';

const renderAsset = (asset: string | React.ReactNode) => {
    if (typeof asset === 'string') {
        return <img src={asset} alt="" className={styles.VerifyIcon} />;
    }
    return asset;
};

interface ConsentStatus {
    [key: string]: {
        bureauName: string;
        lastFetchedAt: Date;
        isConsentGiven: boolean;
    };
}

export interface IDirectorDetails {
    directorName: string;
    phoneNo: string;
    consentStatus?: ConsentStatus;
    lastInviteSentAt?: string;
    isNewDir?: boolean;
    isLoading?: boolean;
    isError: boolean;
    errorMessage: string;
    fieldErrors?: Record<string, string>;
    updatedAt?: number;
    cibilConsentStatus?: ConsentStatus;
    pan?: string;
    directorId?: string;
}

interface CreditBureauConfig {
    displayName: string;
    name: string;
    styles?: {
        logoStyles?: {
            width?: string;
        };
    };
    logoUrl?: string;
    isDirectorInputToBeTaken: boolean;
    heading?: string;
    subHeading?: string;
    // `dataToDisplay` is the per-field config keyed by field name (e.g. {"pan": {isEditable:false}, "phoneNo": {isEditable:true}}).
    // Keys drive FE rendering (which inputs to show); `isEditable` drives whether the input is required/enabled vs read-only/greyed.
    dataToDisplay: Record<string, { isEditable: boolean }>;
    placeholder?: string;
    isActive: boolean;
    isDefault: boolean;
}

interface DirectorDetailsProps {
    isAdminSide?: boolean;
    setConsent?: (consent: boolean) => void;
    creditBureauConfig: CreditBureauConfig;
    directorDetails: IDirectorDetails[];
    setDirectorDetails: React.Dispatch<React.SetStateAction<Record<string, IDirectorDetails[]>>>;
    giveConsent?: (directorId: string, input?: { pan?: string; mobileNumber?: string }) => void;
    latestConsentsByDirectorId?: Record<string, { consenterName: string; consentedAt: string }>;
    receivedDirectorIds?: Set<string>;
    // Forwarded to DirectorInputField — when the current borrower-side session
    // is actually an internal "View Account" (summon) session, Give Consent
    // must render disabled so internal users can't capture consent on behalf
    // of the borrower.
    disableGiveConsent?: boolean;
    // RECUR-27297 — per-PNO "consent mandatory" config; when true, PNO Fetch/Refetch
    // renders disabled for any director with no consent on file.
    consentRequired?: boolean;
    isBulkLoading?: boolean;
    giveConsentForAll?: (directorIds: string[]) => void;
}
function DirectorDetails({
    isAdminSide,
    setConsent,
    creditBureauConfig,
    directorDetails,
    setDirectorDetails,
    giveConsent,
    latestConsentsByDirectorId,
    receivedDirectorIds,
    disableGiveConsent,
    consentRequired,
    isBulkLoading,
    giveConsentForAll,
}: DirectorDetailsProps) {
    const dispatch = useDispatch();
    const managementList = useAppSelector((state: any) => state.company?.uploadJourneyData?.cibilSoftPullData?.managementList);

    const { id } = useParams();
    const userData = useAppSelector((state) => state.user.userData);
    const [disableSendLink, setDisableSendLink] = useState<boolean>(true);

    // Shared by saveDirector and sendLink: PAN is required to ADD a
    // director on every bureau. Returns '' when there's nothing to block, else the field error.
    const getNewDirectorPanError = (isNewDir: boolean | undefined, pan?: string): string => {
        if (!isNewDir) return '';
        if (!pan?.trim()) return 'PAN is required';
        // Normalise before validating (trim + uppercase) so the same PAN is accepted here and in
        // the Management Profile Add flow — validatePAN is anchored/uppercase-only, and UW
        // uppercases server-side anyway.
        if (!validatePAN(pan.trim().toUpperCase())) return 'Please enter a valid PAN';
        return '';
    };

    const validateInput = (value: string, name: string) => {
        if (name === 'phoneNo') return numberOnly(value)?.length == 10;
        if (name === 'directorName') return value?.trim() !== '';
        if (name === 'pan') return validatePAN(value);
        return false;
    };

    const dataToDisplay = creditBureauConfig?.dataToDisplay;

    const bureauName = creditBureauConfig?.name;

    const handleChange = (e: string, index: number, name: string) => {
        let value = e;
        if (name === 'phoneNo') {
            value = numberOnly(value);
            if (value.length > 10) {
                return;
            }

            if (value.length === 10 && directorDetails[index].directorName.trim() !== '') {
                setDisableSendLink(false);
            } else {
                setDisableSendLink(true);
            }

            if (value.length < 10 && value.length > 0) {
                setDirectorDetails((prev) => ({
                    ...prev,
                    [bureauName]: prev[bureauName].map((d, i) =>
                        i === index ? { ...d, fieldErrors: { ...d.fieldErrors, phoneNo: 'Please enter a valid phone number' } } : d,
                    ),
                }));
            } else if (value.length == 10 || value.length == 0) {
                setDirectorDetails((prev) => {
                    const updatedBureau = [...prev[bureauName]];
                    updatedBureau[index] = {
                        ...updatedBureau[index],
                        fieldErrors: { ...updatedBureau[index].fieldErrors, phoneNo: '' },
                    };
                    return { ...prev, [bureauName]: updatedBureau };
                });
            }
        } else if (name === 'directorName') {
            if (
                value.trim() !== '' &&
                Object.entries(dataToDisplay ?? {}).every(
                    ([field, cfg]) => !cfg.isEditable || validateInput(directorDetails[index]?.[field], field),
                )
            ) {
                setDisableSendLink(false);
            } else {
                setDisableSendLink(true);
            }
        } else if (name === 'pan') {
            const isPanValid = validatePAN(value);
            if (isPanValid && directorDetails[index].directorName.trim() !== '') {
                setDisableSendLink(false);
            } else {
                setDisableSendLink(true);
            }
            if (!isPanValid && value.length > 0) {
                setDirectorDetails((prev) => {
                    const updatedBureau = [...prev[bureauName]];
                    updatedBureau[index] = {
                        ...updatedBureau[index],
                        fieldErrors: { ...updatedBureau[index].fieldErrors, pan: 'Please enter a valid PAN' },
                    };
                    return { ...prev, [bureauName]: updatedBureau };
                });
            } else if (value.length === 0 && directorDetails[index]?.isNewDir) {
                // A new-dir row's PAN is required for the add; live
                // feedback as soon as the field is cleared, rather than silently accepting an
                // empty PAN. Enforced again at submit (saveDirector/sendLink) as the source of
                // truth. Untouched for an already-saved director (isNewDir=false).
                setDirectorDetails((prev) => {
                    const updatedBureau = [...prev[bureauName]];
                    updatedBureau[index] = {
                        ...updatedBureau[index],
                        fieldErrors: { ...updatedBureau[index].fieldErrors, pan: 'PAN is required' },
                    };
                    return { ...prev, [bureauName]: updatedBureau };
                });
            } else if (isPanValid || value.length == 0) {
                setDirectorDetails((prev) => {
                    const updatedBureau = [...prev[bureauName]];
                    updatedBureau[index] = {
                        ...updatedBureau[index],
                        fieldErrors: { ...updatedBureau[index].fieldErrors, pan: '' },
                    };
                    return { ...prev, [bureauName]: updatedBureau };
                });
            }
        }
        // Clear BE-level row error (isError/errorMessage) on every keystroke. The BE error
        // is tied to the LAST fetch attempt with the previous PAN/phone; once the user
        // edits any field, that result is stale and would be misleading if left displayed.
        // FE field validation errors (fieldErrors) are managed separately above per-field.
        setDirectorDetails((prev) => {
            const nextForBureau = prev[bureauName].map((d, i) =>
                i === index ? { ...d, [name]: value, isError: false, errorMessage: '' } : d,
            );
            return { ...prev, [bureauName]: nextForBureau };
        });
    };
    const removeDirector = (index: number) => {
        setDirectorDetails((prev) => ({
            ...prev,
            [bureauName]: prev[bureauName].filter((_, i) => i !== index),
        }));
    };
    const addDirector = () => {
        Mixpanel.track(COMPANY_CB_ADD_DIRECTOR);
        const newDirector: IDirectorDetails = {
            directorName: '',
            phoneNo: '',
            consentStatus: {},
            lastInviteSentAt: undefined,
            isNewDir: true,
            isError: false,
            errorMessage: '',
            fieldErrors: {},
            isLoading: false,
            pan: '',
        };
        setDirectorDetails((prev) => ({
            ...prev,
            [bureauName]: [...(prev[bureauName] ?? []), newDirector],
        }));
    };

    const getCibilConsentStatus = () => {
        return {
            bureauName: creditBureauConfig.name,
            lastFetchedAt: new Date(),
            isConsentGiven: true,
        };
    };

    const newConsentStatus: ConsentStatus = { [creditBureauConfig.name]: getCibilConsentStatus() };

    const syncDirectorAcrossBureaus = (
        prevState: Record<string, IDirectorDetails[]>,
        currentBureau: string,
        updatedDirector: IDirectorDetails,
        index: number,
        isNewDirector: boolean = false,
    ): Record<string, IDirectorDetails[]> => {
        const result = {
            ...prevState,
            [currentBureau]: prevState[currentBureau].map((director, idx) => (idx === index ? updatedDirector : director)),
        };

        if (!isNewDirector) return result;

        // Add new director to other bureaus without consent status
        for (const bureau in result) {
            if (bureau !== currentBureau) {
                result[bureau].push({
                    ...updatedDirector,
                    consentStatus: undefined,
                });
            }
        }

        return result;
    };

    /**
     * Save a newly-added director without fetching bureau data (RECUR-27297).
     *
     * Under mandatory consent the fetch can't run for a director who isn't on record yet —
     * consent is keyed by a directorId, and nothing mints one until the director is saved. So
     * the row's first action persists the director; the row then re-renders with a Fetch
     * button, and the normal give-consent-then-fetch flow takes over.
     */
    const saveDirector = (index: number) => {
        // `directorDetails` is this bureau's flat list (IDirectorDetails[]), not a map keyed
        // by bureau — indexing it by bureauName silently yielded undefined and swallowed the save.
        const director = directorDetails?.[index];
        if (!director) return;
        // A second click while the first save is in flight would run onSuccess twice, and each
        // run pushes another copy of this row into every other bureau's list via
        // syncDirectorAcrossBureaus(..., true) — the row only swaps to a spinner, the onClick
        // handler stays live (CodeRabbit).
        if (director.isLoading) return;

        // PAN is required to ADD a director on every bureau. The button stays clickable
        // (DirectorInputField never gates it); this is the authoritative block, since only the
        // parent holds setDirectorDetails to surface the error.
        const panError = getNewDirectorPanError(director.isNewDir, director.pan);
        if (panError) {
            setDirectorDetails((prev) => ({
                ...prev,
                [bureauName]: prev[bureauName].map((d, idx) =>
                    idx === index ? { ...d, fieldErrors: { ...d.fieldErrors, pan: panError } } : d,
                ),
            }));
            return;
        }

        setDirectorDetails((prev) => ({
            ...prev,
            [bureauName]: prev[bureauName].map((d, idx) => (idx === index ? { ...d, isLoading: true } : d)),
        }));

        dispatch(
            addDirectorWithoutFetch({
                assesseeOrgId: id ? id : userData.assesseeOrgId || '',
                directorName: director.directorName,
                pan: director.pan,
                phoneNumber: director.phoneNo,
                onSuccess: (directorId?: string) => {
                    setDirectorDetails((prev) => {
                        const updatedDirector = {
                            ...prev[bureauName][index],
                            isLoading: false,
                            // No longer "new": the row now has a saved director behind it, so the
                            // CTA becomes Fetch and the consent gate applies normally.
                            isNewDir: false,
                            directorId: directorId ?? prev[bureauName][index].directorId,
                            updatedAt: new Date().getTime(),
                            isError: false,
                            errorMessage: '',
                            fieldErrors: {},
                        };
                        return syncDirectorAcrossBureaus(prev, bureauName, updatedDirector, index, true);
                    });
                    toast.success('Director saved. Give consent to fetch their bureau data.');
                },
                onError: (message: string) => {
                    setDirectorDetails((prev) => ({
                        ...prev,
                        [bureauName]: prev[bureauName].map((d, idx) =>
                            idx === index ? { ...d, isLoading: false, isError: true, errorMessage: message } : d,
                        ),
                    }));
                    toast.error(message);
                },
            }),
        );
    };

    const sendLink = (
        name: string,
        setDisableSendLink: (value: boolean) => void,
        index: number,
        source: string,
        flowType?: string,
        phoneNo?: string,
        pan?: string,
    ) => {
        // For a not-yet-persisted director, this Fetch click IS the add action
        // (consentRequired=false — add-and-fetch in one step). PAN is required for the add on
        // every bureau; an already-saved director's Fetch/Refetch is untouched.
        const director = directorDetails[index];
        // In-flight guard: for a new-dir row this Fetch IS the add, and setting isLoading only swaps
        // the row to a spinner — the onClick stays live. Bail on a second click so the add-and-fetch
        // isn't fired twice (dedup race, RCPI-9891 / RECUR-26245), matching saveDirector's guard.
        if (director?.isLoading) return;
        const panError = getNewDirectorPanError(director?.isNewDir, pan);
        if (panError) {
            setDirectorDetails((prev) => ({
                ...prev,
                [bureauName]: prev[bureauName].map((d, idx) =>
                    idx === index ? { ...d, fieldErrors: { ...d.fieldErrors, pan: panError } } : d,
                ),
            }));
            return;
        }

        Mixpanel.track(COMPANY_CB_VERIFY_DIRECTOR);
        setDisableSendLink(false);
        setDirectorDetails((prev) => ({
            ...prev,
            [bureauName]: prev[bureauName].map((director, idx) => (idx === index ? { ...director, isLoading: true } : director)),
        }));

        dispatch(
            sendCibilConsentLink({
                assesseeOrgId: id ? id : userData.assesseeOrgId || '',
                directorName: name,
                phoneNumber: phoneNo,
                email: userData.primaryEmail,
                source: source,
                flowType,
                pan: pan,
                onSuccess: () => {
                    setDirectorDetails((prev) => {
                        const isNewDirector = prev[bureauName][index].isNewDir;
                        const updatedDirector = {
                            ...prev[bureauName][index],
                            isLoading: false,
                            consentStatus: { ...prev[bureauName][index]?.consentStatus, ...newConsentStatus },
                            isNewDir: false,
                            updatedAt: new Date().getTime(),
                            isError: false,
                            errorMessage: '',
                            fieldErrors: {},
                        };
                        return syncDirectorAcrossBureaus(prev, bureauName, updatedDirector, index, isNewDirector);
                    });
                    if (setConsent) {
                        setConsent(true);
                    }
                    setDisableSendLink(true);
                    toast.success('Verification Successful');
                },
                onError: (message: string) => {
                    if (message === 'Error in getting cibil report from Decentro' || message.includes('Inquiry Purpose is invalid')) {
                        toast.error("We're currently facing issues and are resolving them. Please try again later.");
                        setDirectorDetails((prev) => ({
                            ...prev,
                            [bureauName]: prev[bureauName].map((director, idx) =>
                                idx === index ? { ...director, isLoading: false } : director,
                            ),
                        }));
                        setDisableSendLink(true);
                        return;
                    }
                    // Bureau "consumer not found" detection — each bureau returns a different verbatim string:
                    //   Decentro/Equifax legacy: bare "FAILURE"
                    //   TransUnion (ZapData 201): "No Record Found"
                    //   Experian:                 "Consumer Not found in Bureau"
                    // We match on substring (uppercased) and surface a single consistent FE message
                    // ("Consumer Not found in Bureau") via the inline row error — no toast.
                    const upper = (message || '').toUpperCase();
                    const isVendorNoRecord =
                        upper.includes('FAILURE') || upper.includes('NO RECORD') || upper.includes('CONSUMER NOT FOUND');
                    if (isVendorNoRecord) {
                        setDirectorDetails((prev) => ({
                            ...prev,
                            [bureauName]: prev[bureauName].map((director, idx) =>
                                idx === index
                                    ? { ...director, isLoading: false, isError: true, errorMessage: 'Consumer Not found in Bureau' }
                                    : director,
                            ),
                        }));
                        setDisableSendLink(true);
                        return;
                    }
                    toast.error(message || 'Something went wrong');
                    setDirectorDetails((prev) => ({
                        ...prev,
                        [bureauName]: prev[bureauName].map((director, idx) =>
                            idx === index ? { ...director, isLoading: false } : director,
                        ),
                    }));
                    setDisableSendLink(true);
                },
            }),
        );
    };

    const getVerifyIconLink = (director: IDirectorDetails) => {
        if (!director.isNewDir) return VERIFY_GREY_CIRCLE;
        if (Object.entries(dataToDisplay ?? {}).every(([field, cfg]) => !cfg.isEditable || validateInput(director?.[field], field)))
            return <FetchFillIcon colour="var(--primary-text-colour)" height="15" width="15" className={styles.VerifyIcon} />;
        return VERIFY_GREY_CIRCLE;
    };

    const isConsentGiven = (director: IDirectorDetails) => {
        return director?.consentStatus?.[creditBureauConfig?.name]?.isConsentGiven;
    };

    // "Give Consent for All" header CTA (RECUR-27297) — borrower side only. The header and
    // the per-row "Received" badge read the same two signals: the session-local
    // receivedDirectorIds and the server-persisted latestConsentsByDirectorId, so the header
    // never contradicts the individual rows it sits above.
    const eligibleDirectorIds = (directorDetails || []).map((director) => director.directorId).filter(Boolean) as string[];
    const hasEligibleDirectors = eligibleDirectorIds.length > 0;
    const allDirectorsReceived = hasEligibleDirectors && eligibleDirectorIds.every((id) => receivedDirectorIds?.has(id));

    // The bulk payload deliberately EXCLUDES directors that already have a consent on
    // file. The consent write replaces the whole bureauConsent subdocument, so
    // re-sending an already-consented director would advance its consentedAt to now —
    // collapsing every earlier consent to the timestamp of the latest click and
    // destroying the per-director history compliance needs ("X consented Monday,
    // Y consented Friday", not "everyone Friday"). Verified live: re-consenting a
    // director moved consentedAt from 19:18:07 to 19:28:02.
    // Filtering on the SERVER-persisted lastConsent (not the session-local
    // receivedDirectorIds) also self-heals the revocation case — a consent erased by
    // the user-deletion path leaves no lastConsent, so that director is included again.
    const directorIdsNeedingConsent = (directorDetails || [])
        .filter((director) => !!director.directorId && !latestConsentsByDirectorId?.[director.directorId as string])
        .map((director) => director.directorId) as string[];

    // FE-only gate (no BE contract change): "Give Consent for All" must stay disabled until
    // every director STILL NEEDING CONSENT has a valid PAN and Mobile Number — bulk consent
    // shouldn't fire while identity details for one of those rows are missing/invalid. Scoped to
    // directorIdsNeedingConsent, not every rendered row: an already-consented director with a
    // stale/incomplete field shouldn't block consenting the rest.
    const allDirectorsFieldsValid = (directorDetails || [])
        .filter((director) => !!director.directorId && directorIdsNeedingConsent.includes(director.directorId))
        .every((director) =>
            Object.entries(dataToDisplay ?? {}).every(([field, cfg]) => !cfg.isEditable || validateInput(director?.[field], field)),
        );

    const renderGiveConsentForAllCta = () => {
        // Deliberately NOT consentRequired-gated (product call, 2026-08-06): borrowers can
        // always give consent — the flag only controls whether the PNO side USES it (the
        // Fetch/Refetch gate). A consent captured while the flag is off simply sits unused
        // until the PNO is switched on.
        if (!giveConsentForAll || !hasEligibleDirectors) return null;

        // Nothing left to consent — either the badges say so for this session, or every
        // director already has a consent on file from an earlier one.
        if (allDirectorsReceived || directorIdsNeedingConsent.length === 0) {
            return (
                <div className={styles.GiveConsentAllReceived}>
                    <img src={GREEN_CHECK_SMALL} alt="" className={styles.VerifyIcon} />
                    <span>Received for All</span>
                </div>
            );
        }

        if (disableGiveConsent || !allDirectorsFieldsValid) {
            const tooltipMessage = disableGiveConsent
                ? 'You are not allowed to complete this action'
                : 'Please enter valid PAN and Mobile Number for all directors to proceed';
            return (
                <Tooltip
                    fitContent
                    small
                    usePortal
                    content={<span className={styles.GiveConsentTooltipContent}>{tooltipMessage}</span>}
                    direction="top"
                >
                    <div className={clsx(styles.GiveConsentAllCta, styles.GiveConsentAllCtaDisabled)}>
                        <span>Give Consent for All</span>
                    </div>
                </Tooltip>
            );
        }

        const activateGiveConsentForAll = () => {
            if (isBulkLoading) return;
            giveConsentForAll(directorIdsNeedingConsent);
        };
        return (
            <div
                className={styles.GiveConsentAllCta}
                role="button"
                tabIndex={0}
                aria-busy={!!isBulkLoading}
                aria-disabled={!!isBulkLoading}
                onClick={activateGiveConsentForAll}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        activateGiveConsentForAll();
                    }
                }}
            >
                {isBulkLoading ? <LoadingSpinner color="#ffffff" /> : <span>Give Consent for All</span>}
            </div>
        );
    };

    const consentBanner = (
        <div className={styles.MainBanner}>
            {renderAsset(lightBulbBlack)}
            <p className="subHeading">
                By clicking <strong>"Give Consent"</strong> you consent for soft check on credit bureau(s) of following directors on their
                behalf. Don't worry! It does not impact their credit score.
            </p>
        </div>
    );

    return (
        <div>
            {/* RECUR-27297 — Figma has the banner and the consent card as ONE seamless box with no
                gap on the borrower side (also no separate "Credit Bureau" heading — that's covered
                by the tab label above). Not consentRequired-gated: the borrower consent UI renders
                for every borrower (product call, 2026-08-06) — the flag only drives the PNO-side
                Fetch/Refetch gate, so an un-switched PNO just doesn't use what's captured. */}
            {isAdminSide && <p className={styles.Subtitle}>{creditBureauConfig?.heading}</p>}
            <div className={!isAdminSide ? styles.BorrowerCard : ''}>
                {!isAdminSide && consentBanner}
                <div className={!isAdminSide ? styles.BorrowerInnerCard : ''}>
                    {!isAdminSide && (
                        <div className={styles.BorrowerHeaderRow}>
                            <h4 className={styles.SectionHeading}>
                                Give consent for soft check on credit bureau(s) of following directors on their behalf
                            </h4>
                            {renderGiveConsentForAllCta()}
                        </div>
                    )}
                    <div className={styles.DirectorDetailsContainer}>
                        {directorDetails?.map((director, index) => (
                            <div key={index}>
                                <DirectorInputField
                                    director={director}
                                    index={index}
                                    handleChange={handleChange}
                                    sendLink={sendLink}
                                    saveDirector={saveDirector}
                                    errorMessage={director.errorMessage}
                                    isError={director.isError}
                                    fieldErrors={director.fieldErrors}
                                    isLoading={director.isLoading}
                                    isAdminSide={isAdminSide}
                                    placeHolder={creditBureauConfig?.placeholder}
                                    creditBureauConfig={creditBureauConfig}
                                    validateInput={validateInput}
                                    onRemove={director.isNewDir ? () => removeDirector(index) : undefined}
                                    onGiveConsent={
                                        !isAdminSide && !disableGiveConsent && giveConsent
                                            ? () => {
                                                  if (!director.directorId) {
                                                      toast.error(
                                                          director.isNewDir
                                                              ? 'Please save the director first before giving consent.'
                                                              : 'Director ID is missing. Please refresh the page and try again.',
                                                      );
                                                      return;
                                                  }
                                                  giveConsent(director.directorId, {
                                                      pan: director.pan,
                                                      mobileNumber: director.phoneNo,
                                                  });
                                              }
                                            : undefined
                                    }
                                    lastConsent={director.directorId ? latestConsentsByDirectorId?.[director.directorId] : undefined}
                                    // Received when EITHER this session captured it (immediate feedback
                                    // before the refetch lands) OR the server already has a consent on
                                    // file. The server side is what makes this survive a reload — and
                                    // without it the row contradicts the header, which is server-derived:
                                    // a director consented days ago showed "Received for All" up top
                                    // while its own row still invited "Give Consent".
                                    isReceived={
                                        !isAdminSide && director.directorId
                                            ? !!receivedDirectorIds?.has(director.directorId) ||
                                              !!latestConsentsByDirectorId?.[director.directorId]
                                            : false
                                    }
                                    disableGiveConsent={disableGiveConsent}
                                    consentRequired={consentRequired}
                                />
                            </div>
                        ))}
                        {/* Add Director is admin-only — the borrower flow has no save mechanism from
                        this view, so newly-added rows would lack a directorId and the consent click
                        would dead-end on a toast. Hide the CTA on the borrower side until that save
                        flow exists. */}
                        {isAdminSide && (
                            <div className={styles.AddDirText}>
                                Add more directors for credit bureau check.{' '}
                                <span onClick={() => addDirector()} className={styles.AddDirCta}>
                                    Add Director
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default DirectorDetails;
